import { useEffect, useState } from 'react';
import Select from 'react-select';
import { useAppDispatch } from '@store/hooks';
import { getScanningByAssignationIdIdAndScanningType } from '@store/FLEET/FleetScan/fleetScanSlice';
import { Card, CardBody, CardHeader } from '@components/card/card';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Button from 'react-bootstrap/Button';

import PopoverCustom from '@components/Shared/Popover/PopoverCustom';

import useGetEquipmentsList from '@hooks/FLeet/useGetEquipmentsList';
import Loading from '@components/Shared/Loader/Loading';

import Kuroki from '../FleetScan/Kuroki';
import FileUpload from '@components/Files/FileUpload';
import useGetPeoplesList from '@hooks/NTX/useGetPeoplesList';

type props = { scanningType: string; assignationId: string; onclose?: () => void };

const DetilesScan = ({ assignationId, scanningType, onclose }: props) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [attachments, setAttachments] = useState<any[]>([]);
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { OptionsDrivers } = useGetPeoplesList({ LookupType: '' });
  const { OptionsEquipments, loading: loading2 } = useGetEquipmentsList();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // ⏳ بداية التحميل
      setError(null); // 🧹 مسح أي خطأ سابق

      try {
        const res = await dispatch(
          getScanningByAssignationIdIdAndScanningType({
            assignationId,
            scanningType
          })
        ).unwrap();

        if (res.succeeded) {
          setData(res.data);
        } else {
          setData(null);
          setError('فشل في جلب البيانات');
        }
      } catch (err) {
        setData(null);
        setError('حدث خطأ أثناء جلب البيانات');
        console.error(err);
      } finally {
        setLoading(false); // ✅ انتهاء التحميل سواء بنجاح أو خطأ
      }
    };

    if (assignationId && scanningType) {
      fetchData();
    }
  }, [assignationId, scanningType, dispatch]);

  useEffect(() => {
    if (Array.isArray(data?.attachment) && data.attachment.length > 0) {
      const attachmentsList = data.attachment.map((item: any) => ({
        name: item.fileName || '',
        url: item.filePath || ''
      }));
      setAttachments(attachmentsList);
    } else {
      setAttachments([]);
    }
  }, [data]);

  const handleAttachments: React.Dispatch<React.SetStateAction<File[]>> = value => {};

  //________________________________

  // Transform the marker data to the format expected by Kuroki
  const markers = data?.markers.map((m: any) => ({
    x: Number(m.xPosition),
    y: Number(m.yPosition),
    shape: m.shape,
    color: m.color,
    comment: m.comment || '',
    size: Number(m.size)
  }));

  // Create a static formik-like object
  const formik = {
    values: {
      markers: markers
    }
  };

  return (
    <>
      <Loading loading={loading}>
        <CardBody className=''>
          <Form>
            <div className='row mb-4 '>
              <div className='col-md-12 mb-3'>
                <Card className='h-100 '>
                  <CardBody>
                    <div className='row mb-3'>
                      <div className='col-md-4'>
                        <div className='input-group flex-nowrap'>
                          <span className='input-group-text  '>
                            <i className='fas fa-car-side me-2'></i>
                            {t('Vehicle')}
                          </span>
                          <Select
                            isDisabled
                            classNamePrefix='react-select'
                            className='w-100  '
                            isLoading={loading}
                            isSearchable={true}
                            isClearable
                            options={OptionsEquipments.filter(f => !f.IsVacant)}
                            value={OptionsEquipments.find(f => f.value === data?.fleetEquipmentsId)}
                            placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                            formatOptionLabel={option => {
                              const isRTL = i18n.language === 'ar';
                              const imgSrc = option.PathImage || '/assets/img/Image/NoImg2.ico';

                              return (
                                <div className='d-flex justify-content-between align-items-center'>
                                  <div className='d-flex align-items-center'>
                                    <img
                                      src={imgSrc}
                                      alt={option.label}
                                      onError={(e: any) => {
                                        e.target.onerror = null;
                                        e.target.src = '/assets/img/Image/NoImg2.ico';
                                      }}
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        objectFit: 'cover',
                                        marginLeft: isRTL ? '10px' : '0',
                                        marginRight: isRTL ? '0' : '10px',
                                        borderRadius: '4px'
                                      }}
                                    />
                                    <div>{option.label}</div>
                                  </div>
                                </div>
                              );
                            }}
                          />
                        </div>
                      </div>
                      <div className='col-md-4'>
                        <div className='input-group flex-nowrap mb-3'>
                          <span className='input-group-text  '>
                            <i className='fas fa-user	 me-2'></i>
                            {t('Driver')}
                          </span>
                          <Select
                            isDisabled
                            classNamePrefix='react-select'
                            className='w-100  '
                            isLoading={loading}
                            isSearchable={true}
                            isClearable
                            options={OptionsDrivers}
                            value={OptionsDrivers.find(f => f.value === data?.driverId)}
                            placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                          />
                        </div>
                      </div>
                      <div className='col-md-4'>
                        <div className='input-group flex-nowrap'>
                          <span className='input-group-text  '>
                            <i className='far fa-calendar-alt me-2'></i>
                            {t('DeliveryDate')}
                          </span>

                          <Form.Control
                            readOnly
                            type='date'
                            name='scanningDate'
                            value={data?.scanningDate || ''}
                            className='form-control '
                          />
                        </div>
                      </div>
                    </div>

                    <div className='row'>
                      <div className='col-md-4'>
                        <div className='input-group flex-nowrap'>
                          <span className='input-group-text fw-bold'>
                            <i className=' fa-fw bi bi-speedometer fa-lg me-2'></i>
                            {t('Odometer')}
                          </span>
                          <Form.Control
                            type='number'
                            name='odometer'
                            value={data?.odometer || 0}
                            className='form-control w-100'
                            readOnly
                          />
                        </div>
                      </div>

                      <div className='col-md-4'>
                        <div className='input-group flex-nowrap'>
                          <span className='input-group-text   fw-bold'>
                            <i className='fa-fw bi bi-signpost fa-lg me-2'></i>
                            {t('Route No')}
                          </span>
                          <Form.Control
                            type='text'
                            name='description'
                            value={data?.description || ''}
                            className='form-control'
                          />
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            </div>

            {/* Inspection Items Section */}

            <Card className='mb-4'>
              <CardHeader className='  d-flex justify-content-between align-items-center' style={{ cursor: 'pointer' }}>
                <h5 className='mb-0'>
                  <i className='fas fa-clipboard-list me-2'></i>
                  {t('InspectionList')}
                </h5>
                <a className='text-inverse text-opacity-50' data-bs-toggle='collapse' data-bs-target='#completedBoard'>
                  <i className='fa fa-plus-circle'></i>
                </a>
              </CardHeader>
              <div className='collapse ' id='completedBoard'>
                <CardBody>
                  <div className='row'>
                    {data &&
                      data.details &&
                      data?.details
                        ?.slice()
                        .sort((a: any, b: any) => a.elementSort - b.elementSort)
                        ?.map((item: any, index: any) => {
                          const detail = data.details?.[index] ?? {};

                          return (
                            <div key={index} className='col-md-4 mb-2'>
                              <div className='bg-inverse bg-opacity-10 rounded-2 p-2 border hover-highlight d-flex justify-content-between'>
                                <div className='fw-bold mb-0'>
                                  <span className='me-2 text-theme '>{index + 1}.</span>
                                  {i18n.language === 'en' ? item.elementName : item.elementName2}

                                  {item.elementDescription && (
                                    <PopoverCustom
                                      childern={<i className='fas fa-info-circle ms-2'></i>}
                                      Body={i18n.language === 'en' ? item.elementDescription : item.elementDescription2}
                                    />
                                  )}
                                </div>

                                <div className='text-center d-flex gap-2 justify-content-center'>
                                  <Button disabled variant={detail?.value === true ? 'success' : 'outline-success'}>
                                    <i className='fas fa-check '></i>
                                  </Button>

                                  <Button disabled variant={detail?.value === false ? 'danger' : 'outline-danger'}>
                                    <i className='fas fa-times'></i>
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                  </div>
                </CardBody>
              </div>
            </Card>

            {/* Diagram Section */}
            {data?.kuroki && (
              <Card className='mb-4'>
                <CardHeader
                  className='  d-flex justify-content-between align-items-center'
                  style={{ cursor: 'pointer' }}
                >
                  <h5 className='mb-0'>
                    <i className='fas fa-image me-2'></i>
                    {t('VehicleDiagram')}
                  </h5>
                  <a className='text-inverse text-opacity-50' data-bs-toggle='collapse' data-bs-target='#Kuroki'>
                    <i className='fa fa-plus-circle'></i>
                  </a>
                </CardHeader>
                {/* <div className='collapse show' id='Kuroki'> */}
                <div className='collapse' id='Kuroki'>
                  <CardBody>
                    <Kuroki formik={formik} KurokiImage={data.kuroki.filePath ?? ''} viewOnly />
                  </CardBody>
                </div>
              </Card>
            )}

            {/* Notes & Attachments Section */}
            <div className='row mb-4'>
              <div className='col-md-6 mb-3'>
                <Card className='h-100'>
                  <CardHeader className=' '>
                    <h5 className='mb-0'>
                      <i className='fas fa-edit me-2'></i>
                      {t('Notes')}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <Form.Control
                      as='textarea'
                      rows={5}
                      name='description'
                      value={data?.description || ''}
                      placeholder={t('AddYourNotesHere')}
                      className='h-100'
                      readOnly
                    />
                  </CardBody>
                </Card>
              </div>

              <div className='col-md-6 mb-3'>
                <Card className='h-100'>
                  <CardHeader className=' '>
                    <h5 className='mb-0'>
                      <i className='fas fa-paperclip me-2'></i>
                      {t('Attachments')}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <FileUpload attachments={attachments} setAttachments={handleAttachments} />
                  </CardBody>
                </Card>
              </div>
            </div>

            {/* Action Buttons */}

            <div className='d-flex justify-content-center gap-3 mt-3'>
              <button
                type='button'
                className='btn btn-lg  btn-theme mb-1 w-100 rounded-0'
                onClick={() => {
                  const newWindow = window.open(
                    `/FLEET/Print/${assignationId}/${scanningType}`,
                    '_blank',
                    'width=1000,height=800'
                  );
                  newWindow?.focus();
                }}
              >
                <i className='bi bi-printer me-2'></i>
                {t('Print')}
              </button>
              {onclose && (
                <button
                  type='button'
                  className='btn btn-lg  btn-default mb-1 w-100 rounded-0'
                  onClick={() => {
                    onclose();
                  }}
                >
                  <i className='fas fa-times me-2'></i>
                  {t('Cancel')}
                </button>
              )}
            </div>
          </Form>
        </CardBody>
      </Loading>
    </>
  );
};

export default DetilesScan;
